import type { PropTypes } from './LeaderboardSidebar.props';
import type { ReactElement } from 'react';

import { Card, Flex, Skeleton } from '@rango-dev/whale';
import { CircleDollarSign, CircleStar } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '../../../hooks/reduxHooks';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import { AllTimeLeaderboardTab, LeaderboardViewType } from '../../../types';
import { shortenUsername } from '../../Profile/Leaderboard/Leaderboard.helpers';
import {
  displayUsdValue,
  displayUserRank,
} from '../../Profile/ProfileSummary/ProfileSummary.helpers';
import { LeaderboardCtaCard } from '../LeaderboardCtaCard';

import { getCurrentUserData } from './LeaderboardSidebar.helpers';
import { LeaderboardTopContent } from './LeaderboardTopContent';

const SCORE_ICON_SIZE = 24;

export function LeaderboardSidebar(props: PropTypes): ReactElement {
  const { activeTab, viewType, campaignId } = props;
  const { isTablet, isMobile } = useWindowDimensions();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isSmall = isTablet || isMobile;
  const isLoggedIn = useAppSelector((state) => state.profile.isLoggedIn);
  const { data: profileSummary, loading: loadingUserProfile } = useAppSelector(
    (state) => state.profile.profileSummary.private,
  );
  const campaignUserData = useAppSelector((state) =>
    campaignId ? state.campaign.entries[campaignId]?.userData?.data : undefined,
  );
  const loadingCampaignUserData = useAppSelector((state) =>
    campaignId ? state.campaign.entries[campaignId]?.userData?.loading : true,
  );
  const loadingCampaignDynamicData = useAppSelector((state) =>
    campaignId ? state.campaign.entries[campaignId]?.dynamicData?.loading : true,
  );

  const isAllTimeView = viewType === LeaderboardViewType.ALL_TIME;
  const isUserDataLoading = isAllTimeView
    ? loadingUserProfile
    : loadingCampaignUserData || loadingCampaignDynamicData || !campaignUserData;
  const currentUserData = getCurrentUserData(viewType, profileSummary, campaignUserData);
  const username = profileSummary?.username ?? '';

  let score: number;
  if (activeTab === AllTimeLeaderboardTab.REFERRAL) {
    score = profileSummary?.referralVolume ?? 0;
  } else if (activeTab === AllTimeLeaderboardTab.SCORE) {
    score = profileSummary?.score ?? 0;
  } else {
    score = campaignUserData?.score ?? 0;
  }

  const formattedScore =
    activeTab === AllTimeLeaderboardTab.REFERRAL ? displayUsdValue(score) : displayUserRank(score);

  const level = activeTab === AllTimeLeaderboardTab.SCORE ? profileSummary?.level : undefined;

  const title =
    activeTab === AllTimeLeaderboardTab.REFERRAL
      ? t('Your referral volume')
      : t('Your score and rank');

  const scoreIcon =
    activeTab === AllTimeLeaderboardTab.REFERRAL ? (
      <CircleDollarSign size={SCORE_ICON_SIZE} color="var(--slate-1)" />
    ) : (
      <CircleStar size={SCORE_ICON_SIZE} color="var(--slate-1)" />
    );

  const userRank =
    activeTab === AllTimeLeaderboardTab.REFERRAL
      ? profileSummary?.referralRank
      : currentUserData?.rank;

  const renderBottomCard = (): ReactElement => {
    if (isUserDataLoading && isLoggedIn) {
      return <Skeleton width="100%" height={{ initial: '64px', md: '173px' }} />;
    }
    if (!isLoggedIn) {
      return (
        <LeaderboardCtaCard
          variant="login"
          onLogin={() => {
            navigate('/profile');
          }}
        />
      );
    }
    return (
      <LeaderboardCtaCard
        variant="rank"
        username={shortenUsername({ username })}
        rank={userRank ?? -1}
        title={title}
        score={formattedScore}
        level={level?.level}
        scoreIcon={scoreIcon}
      />
    );
  };

  return (
    <Flex
      direction="column"
      gap={{ initial: '2', sm: '5' }}
      width={{ initial: '100%', sm: '50%' }}
      maxWidth={{ md: '424px' }}>
      {!isSmall && (
        <Card variant="surface" size="3">
          <LeaderboardTopContent activeTab={activeTab} />
        </Card>
      )}
      {renderBottomCard()}
    </Flex>
  );
}
